import moment from 'moment';
import uniqBy from 'lodash/uniqBy';
import { logConfig } from '../../../configDebug';
import { MARKET_CLOSE_NY_TIME, MARKET_OPEN_NY_TIME, UTC_MOMENT_FORMAT } from '../../constants/date-time.constants';
import { CallStatus } from '../../enums';
import { CharacterEnum } from '../../enums/character.enum';
import lang from '../../language';
import { CacheableCalls } from '../../libSettings';
import { PagedResponse, SimplePagedResponse } from '../../models/common-response-types';
import {
  BaseRatiosParamType,
  BaseStatementSearchParamType,
  BaseSymbolDataWithName,
  Earning,
  RatiosByTypeParamType,
  RatiosPeriodType,
  StatementPeriodType,
  StatementSearchParamType,
  TopGainerAndLoserData,
  MarketCapData,
} from '../../models/fundamental/types';
import { DEFAULT_PAGE_RESPONSE } from '../../models/pageable';
import CallsCache, { ProcessCallFunction } from '../../store-util/calls-cache/CallsCache';
import { PriceChangeDirection } from '../../store-util/priceTypes';
import {
  setTopGainersInitialItems,
  setTopGainersTimeout,
  updateTopGainers,
} from '../../store-util/top-movers/TopGainersCache';
import {
  setTopLosersInitialItems,
  setTopLosersTimeout,
  updateTopLosers,
} from '../../store-util/top-movers/TopLosersCache';
import { TopMoversCaller } from '../../store-util/top-movers/types';
import TradePriceCache, { CalculatedTradePrice } from '../../store-util/TradePriceCache';
import { defaultLogoBase64 } from '../../util/assets/logos';
import { isOneOf } from '../../util/DataHelpers';
import { addDateAndFormatToMarketDataDate } from '../../util/DateTimeHelpers';
import { setCallStatus } from '../../util/error-handling/StatusByCallHelpers';
import { isEmpty } from '../../util/typeGuards';

import {
  LAST_YEARS_COUNT_FOR_ANNUAL_DATA,
  LAST_YEARS_COUNT_FOR_QUARTERS_DATA,
  LAST_YEARS_COUNT_FOR_TTM_DATA,
  PREVIOUS_EARNINGS_FETCH_SIZE,
  PREVIOUS_EARNINGS_MONTHS_BACK,
} from './constants';
import {
  FundamentalCall,
  FundamentalState,
  GetPreviousEarningsPayload,
  SearchEarningsPayload,
} from './types';

const MAX_RETRIES_TRADE_PRICE = 5;
const TRADE_PRICE_RETRIES_DELAY = 1000;

export function updateCompanyAndLogoCacheWithName(stocks: BaseSymbolDataWithName[], state: FundamentalState) {
  stocks?.forEach(item => {
    const { symbol } = item;
    const theLogo = defaultLogoBase64;
    const currentLogoAndName = state.companyLogoAndName[symbol]
                            ?? { logo: theLogo, name: item.name!, url: item.url! };
    const { logo, name, url } = currentLogoAndName;
    if (!logo) currentLogoAndName.logo = logo!;
    if (!name) currentLogoAndName.name = name!;
    if (!url) currentLogoAndName.url = url!;
    if (!state.companyLogoAndName[symbol] || !logo || !name || !url) {
      state.companyLogoAndName[symbol] = currentLogoAndName;
    }
  });
}

const isValidHourMinuteSecondValue = (time: string): boolean => {
  if (!time) {
    return false;
  }

  const pattern = /^(?:2[0-3]|[01]?[0-9]):[0-5][0-9]:[0-5][0-9]$/;
  return pattern?.test(time);
};

const getEarningCalendarDisplayTime = (time: string): string => {
  if (!isValidHourMinuteSecondValue(time)) {
    return lang.commonDiscoverCalenderSectionTimeNotSupplied();
  }

  if (parseInt(time) >= parseInt(MARKET_CLOSE_NY_TIME)) {
    return lang.commonDiscoverCalenderSectionTimeAfterMarketClose();
  }

  if (parseInt(time) < parseInt(MARKET_OPEN_NY_TIME)) {
    return lang.commonDiscoverCalenderSectionTimeBeforeMarketOpen();
  }

  return lang.commonDiscoverCalenderSectionTimeNotSupplied();
};

export const populateEarningCalendarLabels = (payload: PagedResponse<Earning>): PagedResponse<Earning> => {
  if (!payload || isEmpty(payload.content)) {
    return DEFAULT_PAGE_RESPONSE;
  }

  for (const earning of payload.content) {
    earning.displayTime = getEarningCalendarDisplayTime(earning.time);
    earning.displayEpsEst = !earning.epsEst
      ? lang.commonDiscoverCalenderSectionTimeNoEPSEstimate()
      : lang.commonDiscoverCalenderSectionTimeEPSEstimate(earning.epsEst);
    earning.isReported = isEarningReported(earning);
  }

  return payload;
};

export function processCallCache(
  symbols: string,
  filterByCacheCallName: CacheableCalls | null,
  newPendingValue?: boolean,
) {
  if (!symbols) return '';

  let result = '';

  if (filterByCacheCallName != null) {
    let asArray = symbols.split(',');
    const filtered = asArray.filter(symbol => {
      if (newPendingValue != null) {
        const processCallFunction: ProcessCallFunction = (
          newPendingValue
            ? CallsCache.processRequest
            : CallsCache.processResponse
        ) as ProcessCallFunction;
        const { toBeUpdated } = processCallFunction(filterByCacheCallName, symbol);
        return toBeUpdated;
      }

      return CallsCache.checkIfDirty(filterByCacheCallName, symbol);
    });
    result = filtered.join(',');
  }

  return result;
}

export function clearCallCache(symbols: string, filterByCacheCallName: CacheableCalls | null) {
  if (!symbols || filterByCacheCallName == null) {
    return;
  }

  symbols
    .split(CharacterEnum.Comma)
    .map(symbol => symbol.trim())
    .filter(Boolean)
    .forEach(symbol => {
      CallsCache.deleteCache(filterByCacheCallName, symbol);
    });
}

export function setFundamentalCallStatus(
  call: FundamentalCall,
  status: CallStatus,
  state: FundamentalState,
  errorCode?: number,
) {
  setCallStatus(call, FundamentalCall[call], status, state.statusByCall, errorCode);
}

const sortTopMovers = (topMovers: TopGainerAndLoserData[]) => (
  topMovers.sort((a, b) => Number(b.changePercentage?.slice(1, -1)) - Number(a.changePercentage?.slice(1, -1)))
);

function getTopMoversCurrentPrices(topMovers: TopGainerAndLoserData[], caller: TopMoversCaller) {
  const rawData = TradePriceCache.getAll();
  let count = 0;
  const directionValues: PriceChangeDirection[] = caller === 'TopGainers' ? [ '+', '' ] : [ '-', '' ];
  const symbols = topMovers.map(el => el.symbol);
  const updatedTopMovers: TopGainerAndLoserData[] = [];

  symbols.forEach((symbol, index) => {
    const value: CalculatedTradePrice | undefined = rawData[symbol] as any;
    if (value) {
      const {
        currentPrice,
        direction: itemDirection,
        hasInstrumentSnapshotData,
      } = value ?? {};
      const hasCorrectDirection = isOneOf(itemDirection, directionValues);
      if (
        currentPrice != null
        && hasCorrectDirection
        && hasInstrumentSnapshotData
      ) {
        count++;

        updatedTopMovers.push({
          ...topMovers[index],
          price: currentPrice!,
          changePercentage: value.changePercentWithSign,
        });
      }
    }
    return false;
  });

  const availability = symbols.length && count ? count / symbols.length : 0;
  const data = sortTopMovers(updatedTopMovers);

  return { data, availability };
}

/** The interval id for top movers checks */
let topMoversIntervalId: any = {};
const stopInterval = (caller: TopMoversCaller) => {
  const theinterval = topMoversIntervalId[caller];
  if (theinterval) {
    clearInterval(theinterval);
    delete topMoversIntervalId[caller];
  }
};
/**
 * Checks for price availability for each item in topMovers list
 * @param topMovers A list of the items to check
 * @param caller The caller - gainers or losers
 * @param aimedAvailability The minimum availability to reach - 0.1 by default (1 is maximum, 0 is minimum)
 * @param retriesDelay The delay for each retry
 * @param retriesCount Number of retries
 */
export const getPriceForTopMovers = (
  topMovers: TopGainerAndLoserData[],
  caller: TopMoversCaller,
  aimedAvailability: number = 0.1,
  retriesDelay = TRADE_PRICE_RETRIES_DELAY,
  retriesCount = MAX_RETRIES_TRADE_PRICE,
) => {
  stopInterval(caller);

  if (caller === 'TopGainers') {
    setTopGainersInitialItems(topMovers);
  } else {
    setTopLosersInitialItems(topMovers);
  }
  const { enabled: logEnabled, priceFlow: priceFlowEnabled } = logConfig;

  let retries = 0;
  let currentResult = getTopMoversCurrentPrices(topMovers, caller);

  const isAimedAvailabilityReached = currentResult.availability > aimedAvailability;
  const isRetriesLessThanMaximum = (retries <= retriesCount);

  const intervalCallBack = (isSingleCall = false) => {
    currentResult = getTopMoversCurrentPrices(topMovers, caller);
    const { availability, data } = currentResult;

    if (logEnabled && priceFlowEnabled) {
      const { symbol } = data?.[0] ?? {};
      const {
        hasInstrumentSnapshotData, has1DChartData, has3MChartData, hasTradeUpdate, changePercent,
      } = TradePriceCache.get(symbol);
      const singleCall = isSingleCall ? '(single-call) ' : '';
      console.log(`Top-Movers Price Check ${singleCall}(${retries}/${retriesCount}) -- availability:${availability}   symbol-1(${symbol}):${changePercent}${hasInstrumentSnapshotData ? ',IS' : ''}${has1DChartData ? ',1D' : ''}${has3MChartData ? ',3M' : ''}${hasTradeUpdate ? ',trade' : ''} [price-flow]`);
    }

    if (availability >= aimedAvailability) {
      if (caller === 'TopGainers') {
        updateTopGainers(data);
      } else {
        updateTopLosers(data);
      }
      stopInterval(caller);
      return;
    }

    retries++;
    if (retries > retriesCount) {
      if (caller === 'TopGainers') {
        setTopGainersTimeout();
      } else {
        setTopLosersTimeout();
      }
      stopInterval(caller);
    }
  };

  if (!isAimedAvailabilityReached && isRetriesLessThanMaximum) {
    topMoversIntervalId[caller] = setInterval(intervalCallBack, retriesDelay);
  } else {
    intervalCallBack(true);
  }
};

export const filterButtons = [ lang.commonAll(), lang.mobileStocksLabel(), lang.commonSearchForETFsTitle() ];

export const getListEmptyMessage = (selectedFilter: string) => {
  if (selectedFilter === filterButtons[1]) {
    return lang.mobileNoStocksFound(); // Stock
  }
  if (selectedFilter === filterButtons[2]) {
    return lang.commonNoETFsFound(); // ETFs
  }
  return lang.commonSearchForNoSymbolFound(); // symbol
};

export const parseETFName = (name: string) => {
  if (name.includes('-')) {
    const parts = name.split('-');
    return parts[1].trim();
  }

  return name;
};

export const getStatementSearchParamsByPayload = (params: BaseStatementSearchParamType): StatementSearchParamType => {
  const lastYears = params.periodType === StatementPeriodType.ANNUAL
    ? LAST_YEARS_COUNT_FOR_ANNUAL_DATA
    : LAST_YEARS_COUNT_FOR_QUARTERS_DATA;

  return {
    ...params,
    startDate: addDateAndFormatToMarketDataDate(lastYears, 'years'),
  } as StatementSearchParamType;
};

export const getRatiosSearchParamsByPayload = (params: BaseRatiosParamType): RatiosByTypeParamType => {
  let lastYears = LAST_YEARS_COUNT_FOR_TTM_DATA;

  if (params.periodType === RatiosPeriodType.QUARTERLY) {
    lastYears = LAST_YEARS_COUNT_FOR_QUARTERS_DATA;
  }

  if (params.periodType === RatiosPeriodType.ANNUAL) {
    lastYears = LAST_YEARS_COUNT_FOR_ANNUAL_DATA;
  }

  return {
    ...params,
    startDate: addDateAndFormatToMarketDataDate(lastYears, 'years'),
  } as RatiosByTypeParamType;
};

export const getMarketCapSymbolKey = (item: MarketCapData): string =>
  `${item.symbol}-${item.stockExchangeCode || ''}-${item.countryDesignation || ''}`;

/**
 * Appends an incoming market caps page to the one already in the store. Page 0 replaces the
 * list, later pages are concatenated and de-duplicated so a row that shifts between two calls
 * is not listed twice.
 */
export const mergeMarketCapsPage = (
  currentPage: SimplePagedResponse<MarketCapData>,
  incomingPage: SimplePagedResponse<MarketCapData>,
): SimplePagedResponse<MarketCapData> => {
  const { content = [], number, size, totalPages, totalElements } = incomingPage ?? DEFAULT_PAGE_RESPONSE;
  const mergedContent = number === 0 ? content : uniqBy(currentPage.content.concat(content), getMarketCapSymbolKey);

  return {
    content: mergedContent,
    number,
    size,
    totalPages,
    totalElements,
    numberOfElements: mergedContent.length,
  };
};

const EARNING_REPORT_STATUS_REPORTED = 'REPORTED';

export const isEarningReported = (earning: Pick<Earning, 'reportStatus'>): boolean => (
  earning?.reportStatus === EARNING_REPORT_STATUS_REPORTED
);

export type EarningsSurpriseColorState = 'positive' | 'negative' | 'neutral';

export const getEarningsSurpriseColorState = (
  surprise: string | number | null | undefined,
): EarningsSurpriseColorState | undefined => {
  if (surprise === null || surprise === undefined || surprise === '') {
    return undefined;
  }

  const numericSurprise = Number(surprise);
  if (Number.isNaN(numericSurprise)) {
    return undefined;
  }

  if (numericSurprise > 0) return 'positive';
  if (numericSurprise < 0) return 'negative';
  return 'neutral';
};

export const getPreviousEarningsCacheKey = ({ symbol, beforeDate }: GetPreviousEarningsPayload): string =>
  `${symbol}${CharacterEnum.Pipe}${beforeDate}`;

export const buildPreviousEarningsSearchPayload = (
  { symbol, beforeDate }: GetPreviousEarningsPayload,
): SearchEarningsPayload => ({
  symbols: [symbol],
  startDate: moment(beforeDate).subtract(PREVIOUS_EARNINGS_MONTHS_BACK, 'months').format(UTC_MOMENT_FORMAT),
  // endDate excludes the selected event from its own history.
  endDate: moment(beforeDate).subtract(1, 'days').format(UTC_MOMENT_FORMAT),
  size: PREVIOUS_EARNINGS_FETCH_SIZE,
  // Newest-first + size selects the LATEST rows of the window.
  sortAscending: false,
});
